import React from 'react';
import {IndexRoute, Route, Redirect} from 'react-router';
import AdminPage from 'components/pure/admin/AdminPage';
import {
  adminCheck,
  adminHomeRedirectChange,
  adminHomeRedirectEnter,
  authCheck,
} from 'components/routes/Router.utils';
import UserProfileContainer from 'components/pure/admin/profile/UserProfileContainer';
import TopicsPage from 'components/admin/topics/TopicsPage';
import NewTopicFormContainer from 'components/admin/topics/NewTopicFormContainer';
import ExistingTopicFormContainer from 'components/admin/topics/ExistingTopicFormContainer';
import TriggersPageContainer from 'components/triggers/TriggersPageContainer';
import CreateTriggerRule from 'components/triggers/CreateTriggerRule';
import TriggerReadonlyView from 'components/triggers/TriggerReadonlyView';
import EditTriggerRule from 'components/triggers/EditTriggerRule';
import SmartAlertEditDetails from 'components/triggers/SmartAlertEditDetails';
import AdminEditUserContainer from 'components/stateful/admin/user/AdminEditUserContainer';
import DepartmentsPageContainer from 'components/pure/admin/DepartmentsPageContainer';
import DepartmentEditPageContainer from 'components/pure/admin/DepartmentEditPageContainer';
import DepartmentContextContainer from 'components/pure/admin/DepartmentContextContainer';
import ArchivingProfilesPageContainer from 'components/pure/admin/ArchivingProfilesPageContainer';
import EditArchivingProfilePageContainer from 'components/pure/admin/EditArchivingProfilePageContainer';
import CardProfilesPageContainer from 'components/pure/admin/CardProfilesPageContainer';
import EditCardProfilePageContainer from 'components/pure/admin/EditCardProfilePageContainer';
import CardReasonsPageContainer from 'components/pure/admin/CardReasonsPageContainer';
import CardLabelsPageContainer from 'components/pure/admin/CardLabelsPageContainer';
import TeamsPageContainer from 'components/pure/admin/TeamsPageContainer';
import AdminWelcomeEmailContainer from 'components/stateful/admin/AdminWelcomeEmailContainer';
import LibraryManagementContainer from 'components/pure/admin/LibraryManagementContainer';
import {ConnectPage} from 'components/ConnectPage';
import CoachingReviewTypePageContainer from 'components/pure/admin/CoachingReviewTypePageContainer';
import ReviewTypeEditPageContainer from 'components/pure/admin/ReviewTypeEditPageContainer';
import AdminUserListContainer from '@components/admin/AdminUserListContainer';
import AdminUserActions from '@store/admin/AdminUserActions';
import {featureFlags} from 'utils/const';
import HasFlag from '$pages/@common/has-flag';
import JobList from '$pages/$jobs/$';
import {KpiAdminPage} from '$pages/$kpis/KpiAdminPage';
import CreateKpiGoalForm from '$pages/$kpis/$create';
import EditKpiGoalForm from '$pages/$kpis/$edit';
import AssignmentForm from '$pages/$kpis/$assign';
import AdminGumUserDeepLink from '@components/admin/AdminGumUserDeepLink';

/**
 * All routes under /admin
 */
export const adminRoute = (
  <Route
    path="admin"
    component={AdminPage}
    onEnter={adminHomeRedirectEnter}
    onChange={adminHomeRedirectChange}>
    <Route path="profile" component={UserProfileContainer} />
    {/* Temp redirect for email compatibility. Remove after 12/1/2023 */}
    <Redirect from="profile-v2" to="profile" />
    <Route path="topics">
      <IndexRoute component={TopicsPage} />
      <Route path="new" component={NewTopicFormContainer} />
      <Route path=":filterWordId" component={ExistingTopicFormContainer} />
    </Route>
    <Route path="actions">
      <IndexRoute component={TriggersPageContainer} />
      <Route path="create" component={CreateTriggerRule} />
      <Route path=":ruleId" component={TriggerReadonlyView} />
      <Route path=":ruleId/edit" component={EditTriggerRule} />
      <Route path=":ruleId/edit-details" component={SmartAlertEditDetails} />
    </Route>
    <Route path="kpis" component={HasFlag} flag={featureFlags.adminKpis}>
      <IndexRoute component={KpiAdminPage} />
      <Route path="assignment" component={KpiAdminPage} />
      <Route path="assign/:kpiId" component={AssignmentForm} />
      <Route path="assign/:kpiId/edit/:assignmentId" component={AssignmentForm} />
      <Route path="create" component={CreateKpiGoalForm} />
      <Route path="edit/:kpiId" component={EditKpiGoalForm} />
    </Route>
    <Route path="jobs" component={HasFlag} flag={featureFlags.adminKpis}>
      <IndexRoute component={JobList} />
    </Route>
    <Route onEnter={adminCheck}>
      <Route path="gum">
        <Route path="user/:globalUserId" component={AdminGumUserDeepLink} />
      </Route>
      <Route path="users">
        {/* Force loading state so the table doesn't show stale data on first render */}
        <IndexRoute component={AdminUserListContainer} onEnter={AdminUserActions.setLoading} />
        <Route path="new" component={AdminEditUserContainer} />
        <Route path="edit(/:userId)" component={AdminEditUserContainer} />
      </Route>
      <Route path="departments">
        <IndexRoute component={DepartmentsPageContainer} />
        <Route path="edit(/:departmentId)" component={DepartmentEditPageContainer} />
        <Route path=":departmentId" component={DepartmentContextContainer}>
          <Route path="archivingProfiles">
            <IndexRoute component={ArchivingProfilesPageContainer} />
            <Route path="edit/:archivingProfileId" component={EditArchivingProfilePageContainer} />
            <Route path="new" component={EditArchivingProfilePageContainer} />
          </Route>
          <Route path="cardProfiles">
            <IndexRoute component={CardProfilesPageContainer} />
            <Route path="edit/:cardsProfileId" component={EditCardProfilePageContainer} />
            <Route path="new" component={EditCardProfilePageContainer} />
          </Route>
        </Route>
      </Route>
      <Route path="archivingProfiles">
        <IndexRoute component={ArchivingProfilesPageContainer} />
        <Route path="edit/:archivingProfileId" component={EditArchivingProfilePageContainer} />
        <Route path="new" component={EditArchivingProfilePageContainer} />
      </Route>
      <Route path="cardReasons" component={CardReasonsPageContainer} />
      <Route path="cardSchemes" component={CardLabelsPageContainer} />
      <Route path="cardProfiles">
        <IndexRoute component={CardProfilesPageContainer} />
        <Route path="edit/:cardsProfileId" component={EditCardProfilePageContainer} />
        <Route path="new" component={EditCardProfilePageContainer} />
      </Route>
      <Route path="teams">
        <IndexRoute component={TeamsPageContainer} />
      </Route>
      <Route path="welcome-email" component={AdminWelcomeEmailContainer} />
      <Route path="libraryManagement" component={LibraryManagementContainer} />
      <Route path="integrations" component={ConnectPage} onEnter={authCheck} />
      <Route path="sessionTypes">
        <IndexRoute component={CoachingReviewTypePageContainer} />
        <Route path="create" component={ReviewTypeEditPageContainer} />
        <Route path="edit/:typeId" component={ReviewTypeEditPageContainer} />
      </Route>
    </Route>
  </Route>
);
